import { Currency, CurrencyAmount, Token } from '@kodiak-finance/sdk-core'
import { ButtonPrimary } from 'components/Button'
import { LightCard } from 'components/Card'
import Column, { AutoColumn } from 'components/Column'
import { RowBetween } from 'components/Row'
import { useStepLineState, useStepLineStateUpdate } from 'components/StepLine/context/StepLineContext'
import { useToken } from 'hooks/Tokens'
import { KodiakFarmStake } from 'hooks/useKodiakFarm'
import { useEffect, useMemo, useState } from 'react'
import { Text } from 'rebass'
import styled from 'styled-components'
import { KodiakFarm } from 'types/farms'

type WithdrawBalanceConstructorStepProps = {
  selectedShares: CurrencyAmount<Currency> | undefined
  stakes: KodiakFarmStake[] | null | undefined
  defaultActiveStakes?: KodiakFarmStake[] | null | undefined
  farm?: KodiakFarm | null
  onSelect: (props: { stakes: KodiakFarmStake[] }) => void
  next: () => void
}

const StakeItemCard = styled(LightCard)<{ active?: boolean }>`
  opacity: ${({ active }) => (active ? 1 : 0.4)};
  border: ${({ active, theme }) => `1px solid ${active ? theme.textSecondary : theme.backgroundOutline}`};
  cursor: pointer;
  padding: 1rem;
`

type WithdrawBalanceConstructorState = {
  activeStakes: KodiakFarmStake[]
}

function StakeItemView({
  stake,
  activeByDefault,
  token,
}: {
  stake: KodiakFarmStake
  activeByDefault?: boolean
  token: Token | null | undefined
}) {
  const [active, setActive] = useState(activeByDefault || false)
  const { activeStakes = [] } = useStepLineState<WithdrawBalanceConstructorState>()
  const update = useStepLineStateUpdate<WithdrawBalanceConstructorState>()

  const onClick = () => {
    const newActive = !active
    setActive(newActive)
    update({
      activeStakes: [...activeStakes.filter((s) => s.kekId !== stake.kekId), ...(newActive ? [stake] : [])],
    })
  }

  return (
    <StakeItemCard active={active} onClick={onClick}>
      <AutoColumn>
        <RowBetween style={{ userSelect: 'none' }}>
          <Text>
            {stake.liquidity?.toSignificant(6)} {token?.symbol}
          </Text>
          <Text>x{stake.lockMultiplier.toFixed(2)}</Text>
        </RowBetween>
      </AutoColumn>
    </StakeItemCard>
  )
}

function useRemaining(
  selectedShares: CurrencyAmount<Currency> | undefined,
  defaultActiveStakes: KodiakFarmStake[] | null = [],
  token: Token | null | undefined
) {
  const { activeStakes = [] } = useStepLineState<WithdrawBalanceConstructorState>()
  const update = useStepLineStateUpdate<WithdrawBalanceConstructorState>()

  useEffect(() => {
    if (!defaultActiveStakes || defaultActiveStakes.length === 0) return
    update({ activeStakes: defaultActiveStakes })
  }, [])

  const totalActiveStakesLiquidity = useMemo(() => {
    if (!activeStakes.length || !token) return undefined

    return activeStakes.reduce((total, stake) => {
      if (!stake.liquidity) return total
      return total ? total.add(stake.liquidity) : stake.liquidity
    }, undefined as CurrencyAmount<Token> | undefined)
  }, [activeStakes, token])

  const remaining = useMemo(() => {
    if (!selectedShares || !totalActiveStakesLiquidity) return undefined

    const diff = selectedShares.subtract(totalActiveStakesLiquidity)
    if (diff.lessThan(0)) {
      return undefined // Over-selected
    }
    return diff
  }, [selectedShares, totalActiveStakesLiquidity])

  return {
    totalActiveStakesLiquidity,
    activeStakes,
    remaining,
  }
}

export default function WithdrawBalanceConstructorStep({
  selectedShares,
  stakes,
  farm,
  onSelect,
  next,
  defaultActiveStakes,
}: WithdrawBalanceConstructorStepProps) {
  const stakingToken = useToken(farm?.stakingToken.id)
  const filteredStakes = stakes?.filter((stake) => !!stake) || []

  const remaining = useRemaining(selectedShares, defaultActiveStakes, stakingToken)

  const onWithdraw = () => {
    onSelect({
      stakes: remaining.activeStakes,
    })
    next()
  }

  const continueDisabled = !remaining?.remaining || remaining.remaining.greaterThan(0)

  return (
    <Column gap="8px" width="100%">
      {filteredStakes.map((item) => (
        <StakeItemView
          key={item.kekId}
          stake={item}
          token={stakingToken}
          activeByDefault={defaultActiveStakes?.some((s) => s.kekId === item.kekId)}
        />
      ))}

      <LightCard className="p-4">
        <RowBetween>
          <Text>Selected Amount</Text>
          <Text>
            {remaining?.totalActiveStakesLiquidity?.toSignificant(6)} {stakingToken?.symbol}
          </Text>
        </RowBetween>

        {remaining?.remaining && (
          <RowBetween>
            <Text>Remaining</Text>
            <Text>
              {remaining.remaining.toSignificant(6)} {stakingToken?.symbol}
            </Text>
          </RowBetween>
        )}
      </LightCard>

      <ButtonPrimary disabled={continueDisabled} onClick={onWithdraw}>
        {continueDisabled ? 'Insufficient amount selected' : 'Unstake'}
      </ButtonPrimary>
    </Column>
  )
}
